import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
import { useLazyQuery } from '@apollo/client';

import { createClientsReportApolloClient } from '@seller-area/contexts/clients-report/apollo-client';
import { useAuth } from '@seller-area/wrappers/auth-wrapper';

import { buildGruposReportGraphqlVariables } from './build-report-params';
import { EMPTY_NULL_KPIS, GruposNullKpiKey } from './null-kpi-config';
import { CLARKE_GROUPS_REPORT_QUERY } from './queries';
import { GroupReportRow, GruposReportContextType, GruposReportFilters, GruposReportIndicators } from './types';

const DEFAULT_INDICATORS: GruposReportIndicators = {
  totalGroups: 0,
  totalCompanies: 0,
  totalUnits: 0,
  totalVolumeMwm: 0,
  nullKpis: EMPTY_NULL_KPIS,
};

const DEFAULT_FILTERS: GruposReportFilters = {
  term: '',
  nameFilter: '',
  clientIdFilter: '',
  legalNameFilter: '',
  docType: null,
  docNumberFilter: '',
  exposure: null,
  profile: null,
  crmIdFilter: '',
  accountManagerIdFilter: null,
  status: 'CUSTOMER',
  clientSinceRange: null,
  churnDateRange: null,
  createdAtRange: null,
  updatedAtRange: null,
  volumeMwmRange: null,
  exposurePercentageRange: null,
  companiesCountRange: null,
  unitsCountRange: null,
};

const GATEWAY_SCHEMA_HINT =
  'Se estiver usando o Gateway, publique o schema (rover) após o deploy do backend. Em local, use VITE_CUSTOMER_API_GRAPHQL apontando para a customer API.';

export const GruposReportContext = createContext<GruposReportContextType>({
  groups: [],
  indicators: DEFAULT_INDICATORS,
  total: 0,
  loading: false,
  queryError: null,
  page: 0,
  limit: 50,
  filters: DEFAULT_FILTERS,
  sortBy: null,
  sortOrder: 'asc',
  activeNullKpi: null,
  setPage: () => undefined,
  setLimit: () => undefined,
  setSort: () => undefined,
  setFilter: () => undefined,
  clearFilter: () => undefined,
  clearAllFilters: () => undefined,
  toggleNullKpi: () => undefined,
  fetchGroups: () => undefined,
});

export const GruposReportProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { loading: authLoading, token } = useAuth();
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const reportClient = useMemo(() => createClientsReportApolloClient(tokenRef), []);

  const [page, setPageState] = useState(0);
  const [limit, setLimitState] = useState(50);
  const [filters, setFiltersState] = useState<GruposReportFilters>(DEFAULT_FILTERS);
  const [groups, setGroups] = useState<GroupReportRow[]>([]);
  const [total, setTotal] = useState(0);
  const [indicators, setIndicators] = useState<GruposReportIndicators>(DEFAULT_INDICATORS);
  const [queryError, setQueryError] = useState<string | null>(null);
  const [sortBy, setSortByState] = useState<string | null>(null);
  const [sortOrder, setSortOrderState] = useState<'asc' | 'desc'>('asc');
  const [activeNullKpi, setActiveNullKpi] = useState<GruposNullKpiKey | null>(null);

  const [runQuery, { loading }] = useLazyQuery(CLARKE_GROUPS_REPORT_QUERY, {
    client: reportClient,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const report = data?.clarkeGroupsReport;
      if (report) {
        setQueryError(null);
        setGroups(report.data ?? []);
        setTotal(report.total ?? 0);
        setIndicators(report.indicators ?? DEFAULT_INDICATORS);
      }
    },
    onError: (error) => {
      setGroups([]);
      setTotal(0);
      setIndicators(DEFAULT_INDICATORS);
      const message = error.message?.includes('clarkeGroupsReport')
        ? `Query clarkeGroupsReport indisponível no GraphQL configurado. ${GATEWAY_SCHEMA_HINT}`
        : error.message || 'Não foi possível carregar o relatório de grupos.';
      setQueryError(message);
    },
  });

  const runQueryRef = useRef(runQuery);
  runQueryRef.current = runQuery;

  useEffect(() => {
    if (authLoading || !token) return;

    runQueryRef.current({
      variables: buildGruposReportGraphqlVariables(page, limit, filters, sortBy, sortOrder, activeNullKpi),
    });
  }, [authLoading, token, page, limit, filters, sortBy, sortOrder, activeNullKpi]);

  const fetchGroups = useCallback(() => {
    runQueryRef.current({
      variables: buildGruposReportGraphqlVariables(page, limit, filters, sortBy, sortOrder, activeNullKpi),
    });
  }, [page, limit, filters, sortBy, sortOrder, activeNullKpi]);

  const setSort = useCallback((col: string, order: 'asc' | 'desc') => {
    setSortByState(col);
    setSortOrderState(order);
    setPageState(0);
  }, []);

  const setPage = useCallback((p: number) => setPageState(p), []);
  const setLimit = useCallback((l: number) => {
    setLimitState(l);
    setPageState(0);
  }, []);

  const setFilter = useCallback((key: keyof GruposReportFilters, value: unknown) => {
    setFiltersState((prev) => ({ ...prev, [key]: value }));
    setPageState(0);
  }, []);

  const clearFilter = useCallback((key: keyof GruposReportFilters) => {
    setFiltersState((prev) => ({ ...prev, [key]: DEFAULT_FILTERS[key] }));
    setPageState(0);
  }, []);

  const clearAllFilters = useCallback(() => {
    setFiltersState(DEFAULT_FILTERS);
    setSortByState(null);
    setSortOrderState('asc');
    setActiveNullKpi(null);
    setPageState(0);
  }, []);

  const toggleNullKpi = useCallback((key: GruposNullKpiKey) => {
    setActiveNullKpi((prev) => (prev === key ? null : key));
    setPageState(0);
  }, []);

  const value = useMemo(
    () => ({
      groups,
      indicators,
      total,
      loading: loading || authLoading,
      queryError,
      page,
      limit,
      filters,
      activeNullKpi,
      sortBy,
      sortOrder,
      setPage,
      setLimit,
      setSort,
      setFilter,
      clearFilter,
      clearAllFilters,
      toggleNullKpi,
      fetchGroups,
    }),
    [
      groups,
      indicators,
      total,
      loading,
      authLoading,
      queryError,
      page,
      limit,
      filters,
      activeNullKpi,
      sortBy,
      sortOrder,
      setPage,
      setLimit,
      setSort,
      setFilter,
      clearFilter,
      clearAllFilters,
      toggleNullKpi,
      fetchGroups,
    ],
  );

  return <GruposReportContext.Provider value={value}>{children}</GruposReportContext.Provider>;
};

export const useGruposReport = () => useContextSelector(GruposReportContext, (c) => c);
